import { fetchUrlMetadata } from './fetchUrlMetadata';

const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;

/**
 * Attempts to fetch a favicon for a given URL using multiple fallback services
 * @param url - The full URL or hostname
 * @returns Promise<string> - The favicon URL or fallback icon
 */
export const getFaviconUrl = async (url: string): Promise<string> => {
  try {
    const hostname = new URL(url).hostname;
    
    // List of favicon services to try in order
    const faviconServices = [
      `https://icons.duckduckgo.com/ip2/${hostname}.ico`,
      `https://www.google.com/s2/favicons?domain_url=${hostname}`,
      `https://favicon.yandex.net/favicon/${hostname}`,
      `https://f1.allesedv.com/16/${hostname}`
    ];
    
    // Try each service
    for (const serviceUrl of faviconServices) {
      try {
        await fetch(serviceUrl, { 
          method: 'HEAD',
          mode: 'no-cors' // Allow cross-origin requests
        });
        
        // For no-cors mode, we can't check the actual response status,
        // but we can assume the service is available if no error is thrown
        return serviceUrl;
      } catch (error) {
        // Continue to next service if this one fails
        continue;
      }
    }
    
    // If all services fail, try using our metadata service
    try {
      const metadata = await fetchUrlMetadata(url);
      if (metadata?.image) {
        return metadata.image;
      }
    } catch (error) {
      console.warn('Failed to fetch metadata for favicon:', error);
    }
    
    // Final fallback to our generic link icon
    return `${IMG_CDN}/icons/link_icon.svg`;
    
  } catch (error) {
    console.error('Error getting favicon URL:', error);
    return `${IMG_CDN}/icons/link_icon.svg`;
  }
};

/**
 * Creates an img element with proper error handling for favicons
 * @param url - The URL to get favicon for
 * @param className - CSS classes to apply
 * @param alt - Alt text for the image
 * @returns HTMLImageElement with error handling
 */
export const createFaviconImg = (url: string, className: string = 'size-4 rounded-sm', alt: string = 'Website icon'): HTMLImageElement => {
  const img = document.createElement('img');
  img.className = className;
  img.alt = alt;
  
  let currentServiceIndex = 0;
  const hostname = new URL(url).hostname;
  
  const faviconServices = [
    `https://icons.duckduckgo.com/ip2/${hostname}.ico`,
    `https://www.google.com/s2/favicons?domain_url=${hostname}`,
    `https://favicon.yandex.net/favicon/${hostname}`,
    `https://f1.allesedv.com/16/${hostname}`
  ];
  
  const tryNextService = () => {
    if (currentServiceIndex < faviconServices.length) {
      img.src = faviconServices[currentServiceIndex];
      currentServiceIndex++;
    } else {
      // All services failed, try metadata service or use fallback
      getFaviconUrl(url).then(fallbackUrl => {
        img.src = fallbackUrl;
      }).catch(() => {
        img.src = `${IMG_CDN}/icons/link_icon.svg`;
      });
    }
  };
  
  img.onerror = () => {
    img.onerror = null; // Prevent infinite loops
    tryNextService();
  };
  
  // Start with the first service
  tryNextService();
  
  return img;
};

/**
 * React-friendly favicon URL getter with error handling
 * @param url - The URL to get favicon for
 * @param onError - Optional error callback
 * @returns Object with src and onError handler
 */
export const useFaviconSrc = (url: string) => {
  const hostname = new URL(url).hostname;
  
  const faviconServices = [
    `https://icons.duckduckgo.com/ip2/${hostname}.ico`,
    `https://www.google.com/s2/favicons?domain_url=${hostname}`,
    `https://favicon.yandex.net/favicon/${hostname}`,
    `https://f1.allesedv.com/16/${hostname}`
  ];
  
  let currentServiceIndex = 0;
  
  const createErrorHandler = (imgElement: HTMLImageElement) => {
    return () => {
      imgElement.onerror = null; // Prevent infinite loops
      
      if (currentServiceIndex < faviconServices.length) {
        imgElement.src = faviconServices[currentServiceIndex];
        currentServiceIndex++;
        imgElement.onerror = createErrorHandler(imgElement);
      } else {
        // All services failed, try metadata service
        getFaviconUrl(url).then(fallbackUrl => {
          imgElement.src = fallbackUrl;
        }).catch(() => {
          imgElement.src = `${IMG_CDN}/icons/link_icon.svg`;
        });
      }
    };
  };
  
  return {
    src: faviconServices[0], // Start with DuckDuckGo
    onError: createErrorHandler
  };
};
